/**
 * Sends GA4 `select_item` when a product card is clicked through to its PDP.
 *
 * The Google & YouTube channel sends `view_item_list` for collection pages but
 * nothing on click. This fills the gap in the channel's own shape, because GA4
 * only pairs a click with its list view when both halves agree:
 *
 *   item_id         shopify_ZZ_<product id>_<variant id>
 *   item_list_name  the collection's title, or the homepage section's key
 *
 * Both come from the markup: `data-bh-list` on the list (sections/
 * main-collection.liquid, sections/product-list.liquid) and the variant in the
 * card link's `?variant=`, which assets/product-card.js keeps pointing at the
 * swatch the visitor picked.
 *
 * Sent through the channel's gtag — see bh-gtag.js.
 *
 * Deliberately a delegated listener instead of a change to the ProductCard
 * class: Horizon is an upstream theme and staying outside its classes keeps
 * theme updates painless.
 */

import { itemId, sendEvent } from '@theme/bh-gtag';

const CARD_SELECTOR = 'product-card[view-event-payload], product-card-link[view-event-payload]';

// Mirrors ProductCard#navigateToProduct: a click on one of these is a control,
// not a click-through. Keep in sync with assets/product-card.js.
const INTERACTIVE_SELECTOR = 'button, input, label, select, [tabindex="1"]';

// Names for cards outside any named list, by Horizon's product_view_context
// (see snippets/product-card.liquid).
const CONTEXT_LIST_NAMES = {
  search: 'Search results',
  recommendation: 'Recommended products',
  dialog: 'Menu',
  page: 'Product page',
};

/**
 * @param {Element | null | undefined} element
 * @returns {Record<string, any> | null}
 */
function readPayload(element) {
  const raw = element?.getAttribute('view-event-payload');
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

document.addEventListener(
  'click',
  (event) => {
    if (window.Shopify?.designMode) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest(INTERACTIVE_SELECTOR)) return;

    const card = event.target.closest(CARD_SELECTOR);
    if (!card || card.hasAttribute('data-no-navigation')) return;

    const payload = readPayload(card);
    const product = payload?.product;
    if (!product) return;

    // Whichever list is closest wins: a product list sitting on a collection
    // page is its own list, not the page's.
    const list = card.closest('[data-bh-list]');
    const listName =
      /** @type {HTMLElement | null} */ (list)?.dataset.bhList || CONTEXT_LIST_NAMES[payload.context];

    // Resource cards (search, menu) link without a variant; their id stays
    // product-level, and those contexts have no list view to pair with anyway.
    const href = card.querySelector('a[href]')?.getAttribute('href');
    const variantId =
      product.selectedVariant?.id ?? (href ? new URL(href, location.href).searchParams.get('variant') : null);

    // Scoped by `list ?? document`, so the count restarts at 1 in each section.
    const cards = (list ?? document).querySelectorAll(CARD_SELECTOR);

    sendEvent('select_item', {
      item_list_name: listName,
      items: [
        {
          item_id: itemId(product.id, variantId),
          item_name: product.title,
          index: Array.prototype.indexOf.call(cards, card) + 1,
          item_list_name: listName,
        },
      ],
    });
  },
  { capture: true }
);
